// ═══════════════════════════════════════════════════════════════════════════════
// Previous-pairing import — task / pin quota helpers (§0.50)
// ═══════════════════════════════════════════════════════════════════════════════

import { USER_LEVEL, type PairingImportPreview } from '@fdh/shared';

export const computeSelectedTaskSlotCost = (
  preview: PairingImportPreview,
  selectedTaskIds: ReadonlySet<string>,
  selectedTaskGroupIds: ReadonlySet<string>,
): number => {
  const taskCost = preview.tasks.reduce(
    (sum, item) => (selectedTaskIds.has(item.id) ? sum + item.slotCost : sum),
    0,
  );
  const groupCost = preview.taskGroups.reduce(
    (sum, item) => (selectedTaskGroupIds.has(item.id) ? sum + item.slotCost : sum),
    0,
  );
  return taskCost + groupCost;
};

export const canSelectImportTask = (
  preview: PairingImportPreview,
  taskId: string,
  selectedTaskIds: ReadonlySet<string>,
  selectedTaskGroupIds: ReadonlySet<string>,
): boolean => {
  const task = preview.tasks.find((item) => item.id === taskId);
  if (!task || task.alreadyImported) {
    return false;
  }
  if (selectedTaskIds.has(taskId)) {
    return true;
  }
  if (preview.taskSlotsMax === null) {
    return true;
  }
  const currentCost = computeSelectedTaskSlotCost(preview, selectedTaskIds, selectedTaskGroupIds);
  return preview.taskSlotsUsed + currentCost + task.slotCost <= preview.taskSlotsMax;
};

export const canSelectImportTaskGroup = (
  preview: PairingImportPreview,
  groupId: string,
  selectedTaskIds: ReadonlySet<string>,
  selectedTaskGroupIds: ReadonlySet<string>,
): boolean => {
  const group = preview.taskGroups.find((item) => item.id === groupId);
  if (!group || group.alreadyImported) {
    return false;
  }
  if (selectedTaskGroupIds.has(groupId)) {
    return true;
  }
  if (preview.taskSlotsMax === null) {
    return true;
  }
  const currentCost = computeSelectedTaskSlotCost(preview, selectedTaskIds, selectedTaskGroupIds);
  return preview.taskSlotsUsed + currentCost + group.slotCost <= preview.taskSlotsMax;
};

const computeSelectedPinCounts = (
  preview: PairingImportPreview,
  selectedPinIds: ReadonlySet<string>,
): { total: number; active: number } =>
  preview.pinMessages.reduce(
    (acc, pin) => {
      if (!selectedPinIds.has(pin.id)) {
        return acc;
      }
      return {
        total: acc.total + 1,
        active: acc.active + (pin.isActive ? 1 : 0),
      };
    },
    { total: 0, active: 0 },
  );

export const canSelectImportPin = (
  preview: PairingImportPreview,
  pinId: string,
  selectedPinIds: ReadonlySet<string>,
  userLevel: number | undefined,
): boolean => {
  const pin = preview.pinMessages.find((item) => item.id === pinId);
  if (!pin || pin.alreadyImported) {
    return false;
  }
  if (selectedPinIds.has(pinId)) {
    return true;
  }

  const selected = computeSelectedPinCounts(preview, selectedPinIds);
  const nextTotal = selected.total + 1;
  const nextActive = selected.active + (pin.isActive ? 1 : 0);
  const projectedTotal = preview.pinTotalCount + nextTotal;
  const projectedActive = preview.pinActiveCount + nextActive;

  if (userLevel === USER_LEVEL.FREE) {
    if (projectedTotal > preview.pinMaxItems) {
      return projectedActive <= preview.pinMaxItems;
    }
    return projectedActive <= preview.pinMaxItems;
  }

  return projectedTotal <= preview.pinMaxItems;
};
